// Catálogo de exámenes. Centro Medico → Catálogos → Catálogo de Exámenes.
// Cada examen tiene parámetros (analitos) con valores de referencia.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { AutoTextarea, Campo, CATEGORIA_EXAMEN, Especialidad, Examen, ExamenParametro, Modal, Switch, fieldCls, med, medRpc } from './shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';

const vacio = { codigo: '', nombre: '', categoria: 'laboratorio', subcategoria: '', especialidad_id: '', tipo_muestra: '', modificadores: '', metodo: '', precio: 0, notas: '', es_activo: true, sexo: '' };
// Columnas de texto con filtro por columna.
const COLS_FILTRO = ['nombre', 'codigo', 'categoria', 'subcategoria', 'especialidad', 'muestra'] as const;
// Columnas cuyo filtro es un desplegable con coincidencia EXACTA (no "contiene").
const EXACTAS = ['categoria', 'subcategoria', 'especialidad'];
const valCol = (e: Examen, k: string): string => ({
  nombre: e.nombre, codigo: e.codigo ?? '', categoria: e.categoria, subcategoria: e.subcategoria ?? '',
  especialidad: e.especialidad ?? '', muestra: e.tipo_muestra ?? '',
}[k] ?? '');

export function Examenes() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<Examen[]>({ queryKey: ['med-examenes'], queryFn: () => medRpc<Examen[]>('med_examenes', { _busqueda: '' }) });
  const { data: especialidades = [] } = useQuery<Especialidad[]>({ queryKey: ['med-especialidades'], queryFn: () => med.list<Especialidad>('especialidad', '?select=id,nombre&deleted_at=is.null&order=nombre') });

  const [filtros, setFiltros] = useState<Record<string, string>>({});
  const setF = (k: string, v: string) => setFiltros((f) => ({ ...f, [k]: v }));
  // Desplegables: categoría (todas) y subcategoría en cascada según la categoría elegida.
  const cats = useMemo(() => [...new Set(rows.map((e) => e.categoria).filter(Boolean))].sort(), [rows]);
  const subcats = useMemo(() => [...new Set(
    rows.filter((e) => !filtros.categoria || e.categoria === filtros.categoria)
        .map((e) => e.subcategoria).filter((s): s is string => !!s)
  )].sort(), [rows, filtros.categoria]);
  const esps = useMemo(() => [...new Set(rows.map((e) => e.especialidad).filter((s): s is string => !!s))].sort(), [rows]);
  const filtradas = useMemo(() => rows.filter((e) =>
    Object.entries(filtros).every(([k, v]) => {
      if (!v.trim()) return true;
      const cell = valCol(e, k);
      return EXACTAS.includes(k) ? cell === v : cell.toLowerCase().includes(v.trim().toLowerCase());
    })
  ), [rows, filtros]);
  const sort = useSort<Examen>(filtradas, (r, k) =>
    (k === 'parametros' ? r.parametros.length : k === 'precio' ? r.precio : valCol(r, k)));

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (e?: Examen) => {
    if (e) { setEditId(e.id); setForm({ codigo: e.codigo ?? '', nombre: e.nombre, categoria: e.categoria, subcategoria: e.subcategoria ?? '', especialidad_id: e.especialidad_id ?? '', tipo_muestra: e.tipo_muestra ?? '', modificadores: e.modificadores ?? '', metodo: e.metodo ?? '', precio: e.precio, notas: e.notas ?? '', es_activo: e.es_activo, sexo: e.sexo ?? '' }); }
    else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = { codigo: form.codigo || null, nombre: form.nombre, categoria: form.categoria, subcategoria: form.subcategoria || null, especialidad_id: form.especialidad_id || null, tipo_muestra: form.tipo_muestra || null, modificadores: form.modificadores || null, metodo: form.metodo || null, precio: form.precio, notas: form.notas || null, es_activo: form.es_activo, sexo: form.sexo || null };
      const r = editId ? await med.update('examen_catalogo', editId, body) : await med.create('examen_catalogo', body);
      const created = Array.isArray(r) ? r[0] : r;
      if (!editId && created?.id) setEditId(created.id);
      return created;
    },
    onSuccess: () => { toast.success('Examen guardado'); void qc.invalidateQueries({ queryKey: ['med-examenes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => med.update('examen_catalogo', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { toast.success('Examen eliminado'); void qc.invalidateQueries({ queryKey: ['med-examenes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Catálogo de Exámenes</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo examen</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Exámenes ({sort.sorted.length}{sort.sorted.length !== rows.length ? ` de ${rows.length}` : ''})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <SortTh col="nombre" sort={sort}>Nombre</SortTh>
                <SortTh col="codigo" sort={sort}>Código</SortTh>
                <SortTh col="categoria" sort={sort}>Categoría</SortTh>
                <SortTh col="subcategoria" sort={sort}>Subcategoría</SortTh>
                <SortTh col="especialidad" sort={sort}>Especialidad</SortTh>
                <SortTh col="muestra" sort={sort}>Muestra</SortTh>
                <SortTh col="parametros" sort={sort} align="right">Parámetros</SortTh>
                <SortTh col="precio" sort={sort} align="right">Precio</SortTh>
                <th className="py-2"></th>
              </tr>
              <tr className="border-b">
                {COLS_FILTRO.map((k) => (
                  <th key={k} className="pb-2 pr-3 align-top">
                    {k === 'categoria' ? (
                      <select className={`${fieldCls} h-7 text-xs`} value={filtros.categoria ?? ''} onChange={(ev) => setFiltros((f) => ({ ...f, categoria: ev.target.value, subcategoria: '' }))}>
                        <option value="">Todas</option>
                        {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : k === 'subcategoria' ? (
                      <select className={`${fieldCls} h-7 text-xs`} value={filtros.subcategoria ?? ''} onChange={(ev) => setF('subcategoria', ev.target.value)}>
                        <option value="">Todas</option>
                        {subcats.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : k === 'especialidad' ? (
                      <select className={`${fieldCls} h-7 text-xs`} value={filtros.especialidad ?? ''} onChange={(ev) => setF('especialidad', ev.target.value)}>
                        <option value="">Todas</option>
                        {esps.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <input className={`${fieldCls} h-7 text-xs`} placeholder="Filtrar…" value={filtros[k] ?? ''} onChange={(ev) => setF(k, ev.target.value)} />
                    )}
                  </th>
                ))}
                <th /><th />
                <th className="pb-2 text-right">
                  {Object.values(filtros).some((v) => v.trim()) && <button className="text-[11px] text-primary hover:underline" onClick={() => setFiltros({})}>Limpiar</button>}
                </th>
              </tr>
            </thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-muted-foreground">Sin exámenes</td></tr>}
              {sort.sorted.map((e) => (
                <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{e.nombre}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{e.codigo || '—'}</td>
                  <td className="py-2 pr-3 capitalize">{e.categoria}</td>
                  <td className="py-2 pr-3">{e.subcategoria || '—'}</td>
                  <td className="py-2 pr-3">{e.especialidad || '—'}</td>
                  <td className="py-2 pr-3">{e.tipo_muestra || '—'}</td>
                  <td className="py-2 pr-3 text-right">{e.parametros.length}</td>
                  <td className="py-2 pr-3 text-right">${Number(e.precio).toFixed(2)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar" onClick={() => abrir(e)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 ml-1" title="Eliminar" aria-label="Eliminar" onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar "${e.nombre}"?`, { peligro: true }))) return; eliminar.mutate(e.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar examen' : 'Nuevo examen'} onClose={() => setAbierto(false)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Campo>
              <Campo label="Categoría">
                <select className={fieldCls} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                  {CATEGORIA_EXAMEN.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Campo>
              <Campo label="Subcategoría"><input className={fieldCls} value={form.subcategoria} onChange={(e) => setForm({ ...form, subcategoria: e.target.value })} /></Campo>
              <Campo label="Especialidad">
                <select className={fieldCls} value={form.especialidad_id} onChange={(e) => setForm({ ...form, especialidad_id: e.target.value })}>
                  <option value="">— Ninguna —</option>
                  {especialidades.map((es) => <option key={es.id} value={es.id}>{es.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Tipo de muestra"><input className={fieldCls} value={form.tipo_muestra} onChange={(e) => setForm({ ...form, tipo_muestra: e.target.value })} /></Campo>
              <Campo label="Aplica a (sexo)">
                <select className={fieldCls} value={form.sexo} onChange={(e) => setForm({ ...form, sexo: e.target.value })}>
                  <option value="">Ambos sexos</option>
                  <option value="F">Solo femenino</option>
                  <option value="M">Solo masculino</option>
                </select>
              </Campo>
              <Campo label="Método"><input className={fieldCls} value={form.metodo} onChange={(e) => setForm({ ...form, metodo: e.target.value })} /></Campo>
              <Campo label="Modificadores"><input className={fieldCls} value={form.modificadores} onChange={(e) => setForm({ ...form, modificadores: e.target.value })} /></Campo>
              <Campo label="Precio"><input type="number" step="0.01" className={fieldCls} value={form.precio} onChange={(e) => setForm({ ...form, precio: Number(e.target.value) })} /></Campo>
            </div>
            <Campo label="Notas / indicación"><AutoTextarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></Campo>
            <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />

            {editId && <Parametros examenId={editId} />}

            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cerrar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const paramVacio = { codigo: '', nombre: '', unidad: '', valor_ref_min: '', valor_ref_max: '', valor_ref_texto: '' };

function Parametros({ examenId }: { examenId: string }) {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery<ExamenParametro[]>({
    queryKey: ['med-parametros', examenId],
    queryFn: () => med.list<ExamenParametro>('examen_parametro', `?examen_id=eq.${examenId}&deleted_at=is.null&order=orden,nombre`),
  });
  const [p, setP] = useState<typeof paramVacio>(paramVacio);

  const agregar = useMutation({
    mutationFn: () => {
      // El rango de referencia no puede estar invertido (corrompe el flag alto/bajo).
      if (p.valor_ref_min !== '' && p.valor_ref_max !== '' && Number(p.valor_ref_min) > Number(p.valor_ref_max)) {
        return Promise.reject(new Error('El valor de referencia mínimo no puede ser mayor que el máximo'));
      }
      return med.create('examen_parametro', {
        examen_id: examenId, codigo: p.codigo || null, nombre: p.nombre, unidad: p.unidad || null,
        valor_ref_min: p.valor_ref_min === '' ? null : Number(p.valor_ref_min),
        valor_ref_max: p.valor_ref_max === '' ? null : Number(p.valor_ref_max),
        valor_ref_texto: p.valor_ref_texto || null, orden: rows.length + 1,
      });
    },
    onSuccess: () => { setP(paramVacio); void qc.invalidateQueries({ queryKey: ['med-parametros', examenId] }); void qc.invalidateQueries({ queryKey: ['med-examenes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitar = useMutation({
    mutationFn: (id: string) => med.update('examen_parametro', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['med-parametros', examenId] }); void qc.invalidateQueries({ queryKey: ['med-examenes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <section className="space-y-2">
      <h4 className="text-sm font-semibold text-muted-foreground">Parámetros / valores de referencia</h4>
      {rows.length > 0 && (
        <ul className="text-sm divide-y border rounded-md">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between px-3 py-1.5">
              <span>{r.nombre} {r.unidad && <span className="text-muted-foreground">({r.unidad})</span>} · Ref: {r.valor_ref_texto ?? `${r.valor_ref_min ?? ''}–${r.valor_ref_max ?? ''}`}</span>
              <button className="text-red-500" title="Quitar" aria-label="Quitar" onClick={() => quitar.mutate(r.id)}><Trash2 className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-3"><Campo label="Parámetro"><input className={fieldCls} value={p.nombre} onChange={(e) => setP({ ...p, nombre: e.target.value })} /></Campo></div>
        <div className="col-span-2"><Campo label="Unidad"><input className={fieldCls} value={p.unidad} onChange={(e) => setP({ ...p, unidad: e.target.value })} /></Campo></div>
        <div className="col-span-2"><Campo label="Ref. mín"><input type="number" step="0.01" className={fieldCls} value={p.valor_ref_min} onChange={(e) => setP({ ...p, valor_ref_min: e.target.value })} /></Campo></div>
        <div className="col-span-2"><Campo label="Ref. máx"><input type="number" step="0.01" className={fieldCls} value={p.valor_ref_max} onChange={(e) => setP({ ...p, valor_ref_max: e.target.value })} /></Campo></div>
        <div className="col-span-3"><Campo label="Ref. texto"><input className={fieldCls} value={p.valor_ref_texto} onChange={(e) => setP({ ...p, valor_ref_texto: e.target.value })} placeholder="(opcional)" /></Campo></div>
        <div className="col-span-12"><Button size="sm" variant="outline" isLoading={agregar.isPending} disabled={!p.nombre.trim()} onClick={() => agregar.mutate()}><Plus className="w-4 h-4 mr-1" /> Agregar parámetro</Button></div>
      </div>
    </section>
  );
}
